import React from 'react';
import { TextField, Flex, Text, TextArea, Button } from "@radix-ui/themes";
import Icon from '@mdi/react';
import { mdiContentSave, mdiClose, mdiDelete } from '@mdi/js';
import { api } from '../services/api';
import { useStore } from '../store/store';

export const SidebarCharEdit = (props) => {
    const [state, actions] = useStore();
    const [temporaryCharacter, setTemporaryCharacter] = React.useState(props.selectedCharacter);
    const [focusedField, setFocusedField] = React.useState(null);
    
    React.useEffect(() => {
        setTemporaryCharacter(props.selectedCharacter);
    }, [props.selectedCharacter]);

    const saveCharacter = async () => {
        try {
            // Save to API
            if(temporaryCharacter.new === true) {
                await api.saveCharacters(state.currentNovel.id, [temporaryCharacter]);
            } else {
                await api.updateCharacter(temporaryCharacter);
            }
            
            // Update the novels in the store
            const updatedNovels = state.novels.map(novel => {
                if (novel.characters) {
                    if (temporaryCharacter.new === true) {
                        // Add the new character to the novel's characters array
                        return { ...novel, characters: [...novel.characters, temporaryCharacter] };
                    } else {
                        // Update the existing character in the novel's characters array
                        const updatedCharacters = novel.characters.map(character => 
                            character.id === temporaryCharacter.id ? temporaryCharacter : character
                        );
                        return { ...novel, characters: updatedCharacters };
                    }
                }
                return novel;
            });
            actions.setNovels(updatedNovels);
            
            // Update the current novel in the store if it exists
            if (state.currentNovel) {
                if (temporaryCharacter.new === true) {
                    // Add the new character to the current novel's characters array
                    const updatedCharacters = [...(state.currentNovel.characters || []), temporaryCharacter];
                    
                    const updatedCurrentNovel = {
                        ...state.currentNovel,
                        characters: updatedCharacters
                    };
                    
                    actions.setCurrentNovel(updatedCurrentNovel);
                } else {
                    // Update the existing character in the current novel's characters array
                    const updatedCharacters = state.currentNovel.characters?.map(character => 
                        character.id === temporaryCharacter.id ? temporaryCharacter : character
                    ) || [];
                    
                    const updatedCurrentNovel = {
                        ...state.currentNovel,
                        characters: updatedCharacters
                    };
                    
                    actions.setCurrentNovel(updatedCurrentNovel);
                }
            }

            // Reset the new flag if it was a new character
            if (temporaryCharacter.new) {
                setTemporaryCharacter({...temporaryCharacter, new: false});
            }
        } catch (error) {
            console.error("Failed to save character:", error);
        }
    }

    const deleteCharacter = async () => {
        try {
            // Delete from API
            await api.deleteCharacter(temporaryCharacter.id);
            
            // Update novels in the store by filtering out the deleted character
            const updatedNovels = state.novels.map(novel => {
                if (novel.characters) {
                    return {
                        ...novel,
                        characters: novel.characters.filter(character => character.id !== temporaryCharacter.id)
                    };
                }
                return novel;
            });
            actions.setNovels(updatedNovels);
            
            // Update the current novel in the store
            if (state.currentNovel && state.currentNovel.characters) {
                const updatedCurrentNovel = {
                    ...state.currentNovel,
                    characters: state.currentNovel.characters.filter(character => 
                        character.id !== temporaryCharacter.id
                    )
                };
                actions.setCurrentNovel(updatedCurrentNovel);
            }
            
            // Close the character editor
            props.setSelectedCharacter(null);
        } catch (error) {
            console.error("Failed to delete character:", error);
        }
    }

    return (
        <div className='sidebar-sidebar'>
            <div style={{textAlign: 'right', cursor: 'pointer'}} onClick={() => props.setSelectedCharacter(null)}>
                <Icon path={mdiClose} color={'#FFFFFF50'} size={1} />
            </div>
            <div className="sidebar-form">
                <Flex align={'start'} gap="20px">
                    <Flex direction="column" align={'start'}>
                        <Text size={"2"} className="sidebar-form-textInput-label">Name</Text>
                        {state.readOnly ? (
                            <div className="sidebar-form-textInput read-only-field">{temporaryCharacter?.name ?? ''}</div>
                        ) : (
                            <TextField.Root style={{width: "300px"}} className="sidebar-form-textInput" placeholder="" value={temporaryCharacter?.name ?? ''} variant="soft" onChange={(e) => {
                                setTemporaryCharacter({...temporaryCharacter, name: e.target.value});
                            }} />
                        )}
                    </Flex>
                    <Flex direction="column" align={'start'}>
                        <Text size={"2"} className="sidebar-form-textInput-label">Age</Text>
                        {state.readOnly ? (
                            <div className="sidebar-form-textInput read-only-field">{temporaryCharacter?.age ?? ''}</div>
                        ) : (
                            <TextField.Root className="sidebar-form-textInput" placeholder="" value={temporaryCharacter?.age ?? ''} variant="soft" onChange={(e) => {
                                setTemporaryCharacter({...temporaryCharacter, age: e.target.value});
                            }} />
                        )}
                    </Flex>
                </Flex>
                <br />
                <Text size={"2"} as="div" className="sidebar-form-textInput-label">Role</Text>
                {state.readOnly ? (
                    <div className="sidebar-form-textInput read-only-field">{temporaryCharacter?.role ?? ''}</div>
                ) : (
                    <TextField.Root className="sidebar-form-textInput" placeholder="" value={temporaryCharacter?.role ?? ''} variant="soft" onChange={(e) => {
                        setTemporaryCharacter({...temporaryCharacter, role: e.target.value});
                    }} />
                )}

                <br />

                <Flex justify={'between'} wrap={'wrap'} gap="20px">
                    <Flex direction="column" align={'start'}>
                        <Text size={"2"} as="div" className="sidebar-form-textInput-label">Physical Attributes</Text>
                        {state.readOnly ? (
                            <div className="sidebar-form-textInput read-only-field">{temporaryCharacter?.physical_attributes ?? ''}</div>
                        ) : (
                            <TextArea 
                                className={`sidebar-form-textInput ${focusedField === 'physical_attributes' ? 'textAreaFocus' : ''}`} 
                                placeholder="" 
                                value={temporaryCharacter?.physical_attributes ?? ''} 
                                variant="soft" 
                                onFocus={() => setFocusedField('physical_attributes')}
                                onBlur={() => setFocusedField(null)}
                                onChange={(e) => {
                                    setTemporaryCharacter({...temporaryCharacter, physical_attributes: e.target.value});
                                }} 
                            />
                        )}
                    </Flex>
                    <Flex direction="column" align={'start'}>
                        <Text size={"2"} as="div" className="sidebar-form-textInput-label">Psycological Profile</Text>
                        {state.readOnly ? (
                            <div className="sidebar-form-textInput read-only-field">{temporaryCharacter?.psychological_profile ?? ''}</div>
                        ) : (
                            <TextArea 
                                className={`sidebar-form-textInput ${focusedField === 'psychological_profile' ? 'textAreaFocus' : ''}`} 
                                placeholder="" 
                                value={temporaryCharacter?.psychological_profile ?? ''} 
                                variant="soft" 
                                onFocus={() => setFocusedField('psychological_profile')}
                                onBlur={() => setFocusedField(null)}
                                onChange={(e) => {
                                    setTemporaryCharacter({...temporaryCharacter, psychological_profile: e.target.value});
                                }} 
                            />
                        )}
                    </Flex>
                </Flex>

                <br />

                <Flex justify={'between'} wrap={'wrap'} gap="20px">
                    <Flex direction="column" align={'start'}>
                        <Text size={"2"} as="div" className="sidebar-form-textInput-label">Motivation</Text>
                        {state.readOnly ? (
                            <div className="sidebar-form-textInput read-only-field">{temporaryCharacter?.motivation ?? ''}</div>
                        ) : (
                            <TextArea 
                                className={`sidebar-form-textInput ${focusedField === 'Motivation' ? 'textAreaFocus' : ''}`} 
                                placeholder="" 
                                value={temporaryCharacter?.motivation ?? ''} 
                                variant="soft" 
                                onFocus={() => setFocusedField('Motivation')}
                                onBlur={() => setFocusedField(null)}
                                onChange={(e) => {
                                    setTemporaryCharacter({...temporaryCharacter, motivation: e.target.value});
                                }} 
                            />
                        )}
                    </Flex>
                    <Flex direction="column" align={'start'}>
                        <Text size={"2"} as="div" className="sidebar-form-textInput-label">Inner Monologue Tone</Text>
                        {state.readOnly ? (
                            <div className="sidebar-form-textInput read-only-field">{temporaryCharacter?.inner_monologue_tone ?? ''}</div>
                        ) : (
                            <TextArea 
                                className={`sidebar-form-textInput ${focusedField === 'innermono' ? 'textAreaFocus' : ''}`} 
                                placeholder="" 
                                value={temporaryCharacter?.inner_monologue_tone ?? ''} 
                                variant="soft" 
                                onFocus={() => setFocusedField('innermono')}
                                onBlur={() => setFocusedField(null)}
                                onChange={(e) => {
                                    setTemporaryCharacter({...temporaryCharacter, inner_monologue_tone: e.target.value});
                                }} 
                            />
                        )}
                    </Flex>
                </Flex>

                <br />

                <Flex justify={'between'} wrap={'wrap'} gap="20px">
                    <Flex direction="column" align={'start'}>
                        <Text size={"2"} as="div" className="sidebar-form-textInput-label">Fears</Text>
                        {state.readOnly ? (
                            <div className="sidebar-form-textInput read-only-field">{temporaryCharacter?.fears ?? ''}</div>
                        ) : (
                            <TextArea 
                                className={`sidebar-form-textInput ${focusedField === 'fears' ? 'textAreaFocus' : ''}`} 
                                placeholder="" 
                                value={temporaryCharacter?.fears ?? ''} 
                                variant="soft" 
                                onFocus={() => setFocusedField('fears')}
                                onBlur={() => setFocusedField(null)}
                                onChange={(e) => {
                                    setTemporaryCharacter({...temporaryCharacter, fears: e.target.value});
                                }} 
                            />
                        )}
                    </Flex>
                    <Flex direction="column" align={'start'}>
                        <Text size={"2"} as="div" className="sidebar-form-textInput-label">Miscellaneous</Text>
                        {state.readOnly ? (
                            <div className="sidebar-form-textInput read-only-field">{temporaryCharacter?.miscellaneous ?? ''}</div>
                        ) : (
                            <TextArea 
                                className={`sidebar-form-textInput ${focusedField === 'miscellaneous' ? 'textAreaFocus' : ''}`} 
                                placeholder="" 
                                value={temporaryCharacter?.miscellaneous ?? ''} 
                                variant="soft" 
                                onFocus={() => setFocusedField('miscellaneous')}
                                onBlur={() => setFocusedField(null)}
                                onChange={(e) => {
                                    setTemporaryCharacter({...temporaryCharacter, miscellaneous: e.target.value});
                                }} 
                            />
                        )}
                    </Flex>
                </Flex>
            </div>
            {!state.readOnly && (
                <div>
                    <Flex justify={'between'} gap="20px">
                        <Button color="green" style={{width: '80%', marginTop: '20px', cursor: 'pointer'}} onClick={() => {saveCharacter();}
                        }>
                            <Icon path={mdiContentSave} size={1} />
                            Save Character
                        </Button>
                        <Button color="red" style={{width: 'calc(20% - 20px)', marginTop: '20px', cursor: 'pointer'}} onClick={() => {deleteCharacter();}
                        }>
                            <Icon path={mdiDelete} size={1} />
                        </Button>
                    </Flex>
                </div>
            )}
        </div>
    );
};

export default SidebarCharEdit;